import { FormEvent, useState } from 'react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { signIn, useSession } from 'next-auth/react';
import { useTranslation, Trans } from 'next-i18next';
import { EMAIL_OR_PHONE_PATTERN } from '@auth/constants';
import { LinkStyled, Modal } from '@components/common';
import {
  Button, Input, Label, RadioText,
} from '@components/common/Forms';
import { passwordVisibilityIcons, togglePasswordVisibility, InputPasswordType } from '@auth/forms/formHelpers';
import { clsx } from 'clsx';
import { ForgotPasswordForm } from '@auth/forms/ForgotPasswordForm';

type AuthModalTab = 'login' | 'register' | 'forgotPassword';

interface AuthModalProps {
  locale: Locale;
  initialTab?: AuthModalTab;
}

const tabClasses = {
  common: 'py-4 border-t-3 border-b border-l border-l-ink/10 font-semibold text-sm first:border-l-0',
  active: 'bg-surface text-primary border-b-transparent border-t-secondary',
  inactive: 'bg-secondary/10 border-surface border-b-ink/10 border-t-transparent',
};

export const AuthModal = NiceModal.create((props: AuthModalProps) => {
  const { locale, initialTab = 'login' } = props;
  const modal = useModal();
  const { update } = useSession();
  const { t } = useTranslation('common');
  const [activeTab, setActiveTab] = useState<AuthModalTab>(initialTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordInputType, setPasswordInputType] = useState<InputPasswordType>('password');
  const [wantsBusinessAccount, setWantsBusinessAccount] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  const closeModal = () => {
    modal.reject();
    void modal.hide();
  };

  const switchTab = (tab: AuthModalTab) => {
    setActiveTab(tab);
    setErrorText(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorText(null);
    setIsPending(true);

    try {
      const result = await signIn('credentials', {
        email,
        password,
        walletType: activeTab === 'register' && wantsBusinessAccount ? 'business' : 'personal',
        locale,
        actionType: activeTab,
        redirect: false,
      });

      if (result?.error) {
        setErrorText(activeTab === 'login' ? t('global.wrong.login.data') : t('auth.error.default.message'));
        return;
      }

      await update();
      modal.resolve(true);
      void modal.hide();
    } catch {
      setErrorText(`${t('global.something.went.wrong')}. ${t('auth.error.default.message')}`);
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={closeModal}
      size="sm"
      className="overflow-hidden"
    >
      <div className="grid grid-cols-[1fr_1fr_auto] rounded-t-md">
        <button
          type="button"
          className={clsx(tabClasses.common, activeTab === 'login' ? tabClasses.active : tabClasses.inactive, 'rounded-tl-md')}
          onClick={() => switchTab('login')}
        >
          {t('sign_in.button')}
        </button>
        <button
          type="button"
          className={clsx(tabClasses.common, activeTab === 'register' ? tabClasses.active : tabClasses.inactive, 'rounded-tr-md')}
          onClick={() => switchTab('register')}
        >
          {t('sign_in.register')}
        </button>
        <button
          type="button"
          className={clsx(tabClasses.common, tabClasses.inactive, 'flex w-14 items-center justify-center rounded-tr-md')}
          onClick={closeModal}
          aria-label={t('global.label.close', 'Close')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="size-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 19.5l15-15m-15 0l15 15" />
          </svg>
        </button>
      </div>

      <Modal.Body>
        {activeTab === 'forgotPassword' ? (
          <ForgotPasswordForm locale={locale} className="mt-5.5" />
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-2">
            <div className="relative text-left">
              <Input
                required
                type="text"
                className="asOptional"
                placeholder={t('global.label.email_or_phone')}
                pattern={`^${EMAIL_OR_PHONE_PATTERN}$`}
                patternMismatchMessage={t('auth.email.invalid-format.message')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
              />
            </div>

            <div className="relative text-left">
              <Input
                required
                type={passwordInputType}
                className="asOptional"
                placeholder={t('global.label.password')}
                autoComplete={activeTab === 'register' ? 'new-password' : 'current-password'}
                minLength={6}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <div className="absolute right-1.5 top-2 mt-px">
                <Button
                  type="button"
                  styling="none"
                  className="p-1.5"
                  onClick={() => togglePasswordVisibility(setPasswordInputType)}
                >
                  {passwordVisibilityIcons[passwordInputType]}
                </Button>
              </div>
            </div>

            {activeTab === 'register' && (
              <Label className="relative !mt-0 flex cursor-pointer items-center justify-center rounded bg-secondary/10 py-2">
                <Input
                  type="checkbox"
                  checked={wantsBusinessAccount}
                  onChange={(event) => setWantsBusinessAccount(event.target.checked)}
                />
                <RadioText className="mt-px text-sm leading-none">
                  {t('register.label.hire')}
                </RadioText>
              </Label>
            )}

            {errorText && (
              <div className="mb-2 rounded bg-error/10 p-2 text-sm text-error">
                {errorText}
              </div>
            )}

            <Button
              type="submit"
              color="secondary"
              rounding="full"
              className="mt-2 w-full !font-bold"
              isLoading={isPending}
            >
              {activeTab === 'login' ? t('sign_in.button') : t('register.button')}
            </Button>

            {activeTab === 'login' ? (
              <div className="mt-2 text-center">
                <Button
                  type="button"
                  styling="none"
                  className="text-sm font-semibold text-primary"
                  onClick={() => switchTab('forgotPassword')}
                >
                  {t('register.forgotpass')}
                </Button>
              </div>
            ) : (
              <div className="mt-3 text-sm leading-snug text-ink-medium">
                <Trans
                  i18nKey="register.account.terms.info"
                  components={{
                    anchor: (
                      <LinkStyled
                        styling="text"
                        href="/legal/confidentiality"
                        className="!text-sm"
                      />
                    ),
                  }}
                />
              </div>
            )}
          </form>
        )}
      </Modal.Body>
    </Modal>
  );
});

export const showAuthModal = (props: AuthModalProps) => NiceModal.show(AuthModal, props);
